import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { AuthContext } from '../../contextStore/AuthContext';
import { ToastContext } from '../../contextStore/ToastContext';
import './LinkedAccounts.css';

function LinkedAccounts() {
  const { t } = useTranslation();
  const { user: authUser } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(null);

  if (!authUser) return null;

  const providerIds = (authUser.providerData || []).map((p) => p.providerId);
  const hasGoogle = providerIds.includes('google.com');
  const hasPassword = providerIds.includes('password');
  const canUnlink = providerIds.length > 1;

  const handleLinkGoogle = () => {
    setLinking(true);
    const googleProvider = new firebase.auth.GoogleAuthProvider();
    authUser
      .linkWithPopup(googleProvider)
      .then(() => {
        addToast(t('settings.googleLinked'), 'success');
      })
      .catch((err) => {
        if (err.code === 'auth/credential-already-in-use') {
          addToast(
            t('settings.googleAlreadyLinked'),
            'error'
          );
        } else {
          addToast(err.message || t('settings.linkGoogleFailed'), 'error');
        }
      })
      .finally(() => setLinking(false));
  };

  const handleUnlink = (providerId) => {
    if (!canUnlink) {
      addToast(t('settings.keepAtLeastOneMethod'), 'error');
      return;
    }
    setUnlinking(providerId);
    authUser
      .unlink(providerId)
      .then(() => addToast(t('settings.accountUnlinked'), 'success'))
      .catch((err) => addToast(err.message || t('settings.unlinkFailed'), 'error'))
      .finally(() => setUnlinking(null));
  };

  return (
    <div className="linkedAccountsSection">
      <h3>{t('settings.linkedAccounts')}</h3>
      <p className="linkedAccountsHint">
        {t('settings.linkedAccountsHint')}
      </p>
      <ul className="linkedAccountsList">
        <li className="linkedAccountItem">
          <span className="linkedAccountName">{t('settings.google')}</span>
          {hasGoogle ? (
            <>
              <span className="linkedAccountStatus linked">{t('settings.linked')}</span>
              {canUnlink && (
                <button
                  type="button"
                  className="linkedAccountUnlink"
                  disabled={unlinking === 'google.com'}
                  onClick={() => handleUnlink('google.com')}
                >
                  {unlinking === 'google.com' ? t('settings.unlinking') : t('settings.unlink')}
                </button>
              )}
            </>
          ) : (
            <>
              <span className="linkedAccountStatus">{t('settings.notLinked')}</span>
              <button
                type="button"
                className="linkedAccountLink"
                disabled={linking}
                onClick={handleLinkGoogle}
              >
                {linking ? t('settings.linking') : t('settings.linkGoogle')}
              </button>
            </>
          )}
        </li>
        <li className="linkedAccountItem">
          <span className="linkedAccountName">{t('settings.emailAndPassword')}</span>
          {hasPassword ? (
            <span className="linkedAccountStatus linked">{t('settings.linked')}</span>
          ) : (
            <span className="linkedAccountStatus">{t('settings.notLinked')}</span>
          )}
        </li>
      </ul>
    </div>
  );
}

export default LinkedAccounts;
